import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import QokrQuarterSelect from "../../QuarterlyOkr/_shared/QokrQuarterSelect/QokrQuarterSelect";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import {
  OpcPageError,
  OpcPageLoading,
} from "../_shared/OpcPageState/OpcPageState";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import { quarterName } from "../_config/okrPlansCoachFormat";
import {
  companyPath,
  dashboardPath,
  planPath,
} from "../_config/okrPlansCoachRoutes";
import MemberRow from "./MemberRow/MemberRow";
import "../../_shared/ProjectStyles/Manager.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One company's team (coach) — today's team members page (BE 2.7; CD5, CD6,
// CD16). Quarter dropdown (`?quarter=`, default `is_current`) → ONE members
// call with the plans nested, in the BE's member order. Members are an
// accordion; the first one with plans starts open.
// ═══════════════════════════════════════════════════════════════════════════

const selectCompanies = (s) => s.companies;

const OkrPlansCoachCompany = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const companies = useOkrPlansCoachStore(selectCompanies);
  const selectQuarters = useCallback((s) => s.quarters[companyId], [companyId]);
  const quarters = useOkrPlansCoachStore(selectQuarters);
  const [error, setError] = useState(null);
  const [openIds, setOpenIds] = useState(null);

  const wanted = new URLSearchParams(location.search).get("quarter");
  const quarter = useMemo(() => {
    const list = quarters || [];
    return (
      list.find((q) => String(q.quarter_company_id) === wanted) ||
      list.find((q) => q.is_current) ||
      list[0] ||
      null
    );
  }, [quarters, wanted]);
  const qid = quarter?.quarter_company_id;
  const selectMembers = useCallback(
    (s) => (qid ? s.members[`${companyId}:${qid}`] : null),
    [companyId, qid],
  );
  const members = useOkrPlansCoachStore(selectMembers);

  const loadQuarters = useCallback(() => {
    setError(null);
    const api = okrPlansCoachStore.getState();
    api.fetchQuarters(companyId).catch((err) => setError(err));
    if (!api.companies) api.fetchCompanies().catch(() => {});
  }, [companyId]);
  useEffect(loadQuarters, [loadQuarters]);

  const loadMembers = useCallback(() => {
    if (!qid) return;
    setError(null);
    okrPlansCoachStore
      .getState()
      .fetchMembers(companyId, qid)
      .catch((err) => setError(err));
  }, [companyId, qid]);
  useEffect(() => {
    loadMembers();
    setOpenIds(null);
  }, [loadMembers]);

  const company = (companies?.companies || []).find(
    (c) => String(c.company_id) === String(companyId),
  );
  const name = company?.name || "Company";
  useEffect(() => {
    okrPlansCoachStore
      .getState()
      .setLastCompany({ id: companyId, name, quarterCompanyId: qid });
  }, [companyId, name, qid]);
  const quarterLabel = quarter ? quarterName(quarter) : "";

  let body;
  if (error)
    body = (
      <OpcPageError
        error={error}
        onRetry={members ? loadMembers : loadQuarters}
        onBack={() => history.push(dashboardPath())}
        backLabel="Dashboard"
      />
    );
  else if (!quarters || (qid && !members))
    body = <OpcPageLoading kind="company" />;
  else {
    const team = members?.members || [];
    const firstWithPlans = team.find((m) => (m.plans || []).length);
    const opened =
      openIds === null ? [firstWithPlans?.user_id].filter(Boolean) : openIds;
    const toggle = (id) =>
      setOpenIds(
        opened.includes(id) ? opened.filter((x) => x !== id) : [...opened, id],
      );
    body = (
      <div className="p-3">
        <div className="Header d-flex align-items-start justify-content-between">
          <div className="d-flex flex-column">
            <span className="fw-bold dark-grey">{name}</span>
            <span className="light-text small">
              Open a team member&apos;s OKR plan to add check-ins and review it
            </span>
          </div>
          {quarters.length ? (
            <QokrQuarterSelect
              quarters={quarters.map((q) => ({
                ...q,
                label: quarterName(q),
                run_state: q.state,
              }))}
              selectedId={qid}
              onChange={(id) => history.push(companyPath(companyId, id))}
            />
          ) : null}
        </div>
        <div className="mt-4">
          <span className="fw-bold light-text small">Team members</span>
          <div className="member-card-main">
            {team.length ? (
              team.map((m) => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  open={opened.includes(m.user_id)}
                  quarterLabel={quarterLabel}
                  onToggle={() => toggle(m.user_id)}
                  onOpenPlan={(p) => history.push(planPath(p.plan_unique_id))}
                />
              ))
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center opc-empty">
                <span className="fw-bold dark-grey small">
                  No OKR plans in {quarterLabel || "this quarter"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="opc-screen">
      <OpcHeader
        crumbs={[
          { title: "Dashboard", go: () => history.push(dashboardPath()) },
          { title: name },
        ]}
      />
      <div id="manager-dashboard">{body}</div>
    </div>
  );
};

export default OkrPlansCoachCompany;
